// Every fact the information pages state about the business, in one place.
//
// Payment gateway reviewers cross-check these against the KYC documents and the
// application form, so they must agree with each other and with the bank account.
// Keeping them here means filling them in once, not six times.
//
// ─── THE RULE HERE ───────────────────────────────────────────────────────────
// This file holds only details the client has confirmed. A field left empty is
// left OUT of the pages — no guess, and no "to be filled in" marker either. Fill
// one in and the row, line or section that needs it comes back on its own.
//
// Confirmed by the client on 28 September 2026: the name, the phone number and
// the operating address (the address also checks out against OpenStreetMap —
// Arumuganeri, Tiruchendur taluk, Thoothukudi district, 628202).
//
// ─── STILL OUTSTANDING FOR THE ICICI SUBMISSION ──────────────────────────────
// Empty below, and so absent from the pages. A reviewer is likely to ask for the
// first three, and Indian payment rules expect the fourth:
//
//   legalName   the name as registered, matching the bank account
//   fssai       the licence number a food business is expected to display
//   gstin       if registered
//   GRIEVANCE   a named person a customer can escalate to, with a way to reach them
//
// Also absent because nobody has confirmed them: a monitored email address,
// opening hours, and the courts named in Terms & Conditions.
// ─────────────────────────────────────────────────────────────────────────────

export const BUSINESS = {
    brandName: 'Chenthoor',

    // Full public-facing name, as the restaurant's own listing gives it.
    fullName: 'Chenthoor Restaurant',
    tagline: 'Taste Meets Tradition',

    // Exactly as registered, matching the bank account and KYC.
    legalName: '',

    // Operating address, one line per array entry.
    addressLines: ['124/21, Thiruchendur Road'],
    city: 'Arumuganeri',
    district: 'Thoothukudi',
    state: 'Tamil Nadu',
    postalCode: '628202',
    country: 'India',

    phone: '+91 89258 97061',
    whatsapp: '',
    email: '',

    // Shown when present; a restaurant's FSSAI licence number is expected to be
    // displayed, and GSTIN is expected if registered.
    gstin: '',
    fssai: '',

    // Confirm against the branch's actual hours before filling this in — it is a
    // promise to a customer deciding when to collect an order.
    hours: '',

    // The restaurant's own Google Maps listing, behind the map on Contact us.
    mapLink: 'https://share.google/ij6UNcZgNnquLybvh',

    // The exact pin. Preferred over geocoding the address string, which puts
    // the marker wherever Google guesses — often the wrong end of the road.
    latitude: '8.554138',
    longitude: '78.099000',

    // The public URL these pages will live on, as submitted to ICICI.
    websiteUrl: 'https://chenthoor.hrapprova.com',

    // Named in Terms & Conditions as the courts having jurisdiction. A legal
    // choice, so it waits for the client rather than following the address.
    jurisdictionCity: '',

    currency: 'INR',
    currencySymbol: '₹',

    // The date printed on the policy pages. Update whenever the wording changes.
    lastUpdated: '28 September 2026'
};

// A person a customer can escalate to. Indian payment rules expect a named
// contact with a response commitment, not just a generic inbox. While `name` is
// empty the escalation sections stay off the pages entirely.
export const GRIEVANCE = {
    name: '',
    designation: 'Grievance Officer',
    email: '',
    phone: '',
    responseHours: 48
};

// The cancellation and refund commitments. Pre-orders are cooked to a date, so
// the windows are counted back from the requested date and time.
//
// These are PROPOSALS — commitments the restaurant will be held to, so confirm
// them with the client before this goes live.
export const REFUND = {
    freeCancelHours: 24,     // full refund if cancelled at least this long before
    partialCancelHours: 6,   // between this and freeCancelHours: partial refund
    partialPercent: 50,      // what is refunded in that middle window
    refundDays: '5 to 7 business days',
    failedPaymentDays: '5 to 7 business days'
};

export const DELIVERY = {
    radiusKm: 8,
    chargeNote: '',          // e.g. "₹30 within 3 km, ₹50 beyond" — leave empty if free
    minOrderNote: '',        // e.g. "Minimum order ₹300 for delivery"
    pickupWindowMinutes: 30, // how long a pickup order is held after the chosen time
    preOrderNoticeHours: 24  // how far ahead a special order must be placed
};

// About-page copy. Wording only — nothing a reviewer cross-checks against a
// document, so this is safe to edit freely. Kept deliberately free of claims
// nobody has confirmed: no cuisine or vegetarian-only claim, no ratings, and no
// customer or experience counts.
export const ABOUT = {
    story: 'Chenthoor Restaurant is built on the belief that good food brings '
        + 'people together. We cook with fresh ingredients bought for the day, and '
        + 'we would rather cook a dish to order than keep it waiting.',

    values: [
        { title: 'Quality', detail: 'We never compromise' },
        { title: 'Freshness', detail: 'Cooked to order, not in advance' },
        { title: 'Hygiene', detail: 'Clean, safe and hygienic' },
        { title: 'Customer First', detail: 'Your satisfaction is our priority' }
    ]
};

/** The full address as one string, for a single-line display. */
export const fullAddress = () => BUSINESS.addressLines
    .concat([BUSINESS.city, BUSINESS.district, BUSINESS.state, BUSINESS.postalCode, BUSINESS.country])
    .filter((part) => part)
    .join(', ');

/**
 * The map on Contact us. The classic query embed, which needs no API key and
 * pins whatever address is above — so it follows the address rather than holding
 * coordinates of its own that could drift out of step with it.
 */
// Coordinates when we have them, the address string otherwise. Geocoding an
// address drops the pin wherever Google guesses; coordinates put it exactly
// on the door, which is the whole point of the map on Contact us.
export const mapEmbedUrl = () => {
    const { latitude, longitude } = BUSINESS;
    const query = (latitude && longitude)
        ? `${latitude},${longitude}`
        : fullAddress();

    return 'https://www.google.com/maps?q=' + encodeURIComponent(query) + '&z=17&output=embed';
};
